import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { AuthClient } from "./http/auth-client";
import { RequestClient } from "../request/http/request-client";
import { LocalStorageKeys } from "../../constants";
import type { User } from "@autom8n/users";

export const useAuthStore = defineStore("auth", () => {
  const authClient = new AuthClient();
  const token = ref<string | null>(localStorage.getItem(LocalStorageKeys.AuthToken));

  watch(token, (value) => {
    if (value) {
      localStorage.setItem(LocalStorageKeys.AuthToken, value);
    } else {
      localStorage.removeItem(LocalStorageKeys.AuthToken);
    }
  });
  const user = ref<User | null>(null);

  async function refreshUser() {
    user.value = await authClient.authenticate();
  }

  async function oauth2Authenticate({ code }: { code: string }) {
    const raw = localStorage.getItem(`${LocalStorageKeys.OAUTH}_provider`);

    if (!code || !raw) {
      throw new Error("Missing OAuth data");
    }

    let parsed: { name: string; codeVerifier: string; timestamp: number };
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(`${LocalStorageKeys.OAUTH}_provider`);
      throw new Error("Invalid OAuth provider data");
    }

    const MAX_AGE_MS = 10 * 60 * 1000;
    if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
      localStorage.removeItem(`${LocalStorageKeys.OAUTH}_provider`);
      throw new Error("OAuth session expired");
    }

    try {
      const { token: authToken } = await authClient.oauth2Redirect({
        code,
        provider: { name: parsed.name, codeVerifier: parsed.codeVerifier },
      });
      token.value = authToken;
    } catch (err: unknown) {
      if (err instanceof Error && (err as any).status === 409) {
        // Duplicate external auth records were cleaned up server-side.
        // Re-initiate the OAuth flow with a fresh authorization code.
        localStorage.removeItem(`${LocalStorageKeys.OAUTH}_provider`);
        const providers = await authClient.listOauthProviders();
        const fresh = (providers as any[]).find(
          (p: any) => p.name === parsed.name,
        );
        if (fresh) {
          localStorage.setItem(
            `${LocalStorageKeys.OAUTH}_provider`,
            JSON.stringify({
              name: fresh.name,
              codeVerifier: fresh.codeVerifier,
              timestamp: Date.now(),
            }),
          );
          window.location.href = fresh.authURL;
          return new Promise(() => {}); // never resolves — page is navigating
        }
      }
      throw err;
    } finally {
      localStorage.removeItem(`${LocalStorageKeys.OAUTH}_provider`);
    }

    // Check for a pending request stored before OAuth login
    let redirectUrl = "/";
    try {
      const pendingRaw = localStorage.getItem(LocalStorageKeys.PendingRequest);
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw) as {
          description: string;
          timestamp: number;
        };
        const PENDING_MAX_AGE_MS = 10 * 60 * 1000;
        if (
          pending.description &&
          Date.now() - pending.timestamp <= PENDING_MAX_AGE_MS
        ) {
          const requestClient = new RequestClient();
          const request = await requestClient.submit(pending.description);
          redirectUrl = `/requests/${request.id}`;
        }
      }
    } finally {
      localStorage.removeItem(LocalStorageKeys.PendingRequest);
    }

    // Small delay = smoother UX
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 600);
  }

  async function logout() {
    token.value = null;
    user.value = null;
  }

  async function deleteAccount() {
    await authClient.deleteAccount();
    await logout();
  }

  return {
    token,
    user,
    refreshUser,
    logout,
    deleteAccount,
    oauth2Authenticate,
  };
});
